import { Controller } from "@hotwired/stimulus"

/**
 * GeoJSON Feature Editor
 *
 * Reads GeoJSON from the form textarea (identified by textareaId value),
 * displays a feature properties table, and provides modals to edit
 * individual or all feature properties.
 * Updates are written back to the textarea.
 */
export default class extends Controller {
  static values = { textareaId: String, filenameFieldId: String }
  static targets = ["table", "parseError",
                    "editModal", "editIndex", "editName", "editDescription",
                    "editStroke", "editStrokeColor", "editStrokeWidth", "editStrokeOpacity",
                    "editFill", "editFillColor", "editFillOpacity",
                    "setAllModal",
                    "setAllStrokeCheck", "setAllStroke", "setAllStrokeColor",
                    "setAllStrokeWidthCheck", "setAllStrokeWidth",
                    "setAllStrokeOpacityCheck", "setAllStrokeOpacity",
                    "setAllFillCheck", "setAllFill", "setAllFillColor",
                    "setAllFillOpacityCheck", "setAllFillOpacity",
                    "setAllNameCheck", "setAllName",
                    "setAllDescriptionCheck", "setAllDescription"]

  connect() {
    this.textarea = document.getElementById(this.textareaIdValue)
    this.filenameField = this.filenameFieldIdValue ? document.getElementById(this.filenameFieldIdValue) : null

    this.parseAndRender()

    // Re-parse when textarea changes (manual edits, KML upload, etc.)
    if (this.textarea) {
      this._onInput = () => this.parseAndRender()
      this.textarea.addEventListener('input', this._onInput)
      this.textarea.addEventListener('change', this._onInput)
    }
  }

  disconnect() {
    if (this.textarea && this._onInput) {
      this.textarea.removeEventListener('input', this._onInput)
      this.textarea.removeEventListener('change', this._onInput)
    }
  }

  parseAndRender() {
    if (!this.textarea) return

    const text = this.textarea.value.trim()
    if (!text) {
      this.clearTable()
      this.hideParseError()
      return
    }

    try {
      this.geojson = JSON.parse(text)
      if (!this.geojson.features || !Array.isArray(this.geojson.features)) {
        this.showParseError('GeoJSON must be a FeatureCollection with a features array')
        this.clearTable()
        return
      }
      this.hideParseError()
      this.renderTable()
    } catch (e) {
      this.showParseError(`Invalid JSON: ${e.message}`)
      this.clearTable()
    }
  }

  renderTable() {
    if (!this.hasTableTarget || !this.geojson) return

    const features = this.geojson.features
    if (features.length === 0) {
      this.tableTarget.innerHTML = '<p class="text-muted">No features in GeoJSON</p>'
      return
    }

    let html = `
      <table class="table table-sm table-bordered ap-table">
        <thead>
          <tr>
            <th style="width: 40px">#</th>
            <th>Name</th>
            <th>Description</th>
            <th style="width: 80px">Stroke</th>
            <th style="width: 80px">Fill</th>
            <th style="width: 70px">Action</th>
          </tr>
        </thead>
        <tbody>`

    features.forEach((feature, index) => {
      const props = feature.properties || {}
      const name = props.name || ''
      const desc = props.description || ''
      const stroke = props.stroke || ''
      const fill = props.fill || ''

      html += `
          <tr>
            <td>${index + 1}</td>
            <td>${this.escapeHtml(name) || '<span class="text-muted">—</span>'}</td>
            <td>${this.escapeHtml(this.truncate(desc, 60)) || '<span class="text-muted">—</span>'}</td>
            <td>${stroke ? `<span class="color-swatch" style="background-color: ${this.escapeHtml(stroke)}; display: inline-block; width: 16px; height: 16px; border: 1px solid #ccc; vertical-align: middle;"></span> ${this.escapeHtml(stroke)}` : '<span class="text-muted">—</span>'}</td>
            <td>${fill ? `<span class="color-swatch" style="background-color: ${this.escapeHtml(fill)}; display: inline-block; width: 16px; height: 16px; border: 1px solid #ccc; vertical-align: middle;"></span> ${this.escapeHtml(fill)}` : '<span class="text-muted">—</span>'}</td>
            <td><button type="button" class="btn btn-sm dp-btn-secondary" data-action="geojson-feature-editor#openEditModal" data-index="${index}">Edit</button></td>
          </tr>`
    })

    html += '</tbody></table>'
    this.tableTarget.innerHTML = html
  }

  clearTable() {
    if (this.hasTableTarget) {
      this.tableTarget.innerHTML = ''
    }
  }

  showParseError(message) {
    if (this.hasParseErrorTarget) {
      this.parseErrorTarget.textContent = message
      this.parseErrorTarget.classList.remove('d-none')
    }
  }

  hideParseError() {
    if (this.hasParseErrorTarget) {
      this.parseErrorTarget.classList.add('d-none')
    }
  }

  // --- Edit single feature modal ---

  openEditModal(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index)
    const feature = this.geojson.features[index]
    if (!feature) return

    const props = feature.properties || {}

    this.editIndexTarget.value = index
    this.editNameTarget.value = props.name || ''
    this.editDescriptionTarget.value = props.description || ''
    this.editStrokeTarget.value = this.normalizeColor(props.stroke) || '#555555'
    this.editStrokeColorTarget.value = props.stroke || ''
    this.editStrokeWidthTarget.value = props['stroke-width'] ?? ''
    this.editStrokeOpacityTarget.value = props['stroke-opacity'] ?? ''
    this.editFillTarget.value = this.normalizeColor(props.fill) || '#555555'
    this.editFillColorTarget.value = props.fill || ''
    this.editFillOpacityTarget.value = props['fill-opacity'] ?? ''

    const modal = new bootstrap.Modal(this.editModalTarget)
    modal.show()
  }

  applyEdit() {
    const index = parseInt(this.editIndexTarget.value)
    const feature = this.geojson.features[index]
    if (!feature) return

    if (!feature.properties) feature.properties = {}
    const props = feature.properties

    // Set or clear string properties
    this.setOrClear(props, 'name', this.editNameTarget.value.trim())
    this.setOrClear(props, 'description', this.editDescriptionTarget.value.trim())
    this.setOrClear(props, 'stroke', this.editStrokeColorTarget.value.trim())
    this.setOrClear(props, 'fill', this.editFillColorTarget.value.trim())

    // Set or clear numeric properties
    this.setOrClearNumeric(props, 'stroke-width', this.editStrokeWidthTarget.value)
    this.setOrClearNumeric(props, 'stroke-opacity', this.editStrokeOpacityTarget.value)
    this.setOrClearNumeric(props, 'fill-opacity', this.editFillOpacityTarget.value)

    this.writeBackAndRefresh()
    bootstrap.Modal.getInstance(this.editModalTarget).hide()
  }

  // --- Set All modal ---

  openSetAllModal(event) {
    event.preventDefault()

    // Reset all checkboxes to unchecked
    const checks = [
      this.setAllStrokeCheckTarget, this.setAllStrokeWidthCheckTarget,
      this.setAllStrokeOpacityCheckTarget, this.setAllFillCheckTarget,
      this.setAllFillOpacityCheckTarget, this.setAllNameCheckTarget,
      this.setAllDescriptionCheckTarget
    ]
    checks.forEach(cb => cb.checked = false)

    const modal = new bootstrap.Modal(this.setAllModalTarget)
    modal.show()
  }

  applySetAll() {
    if (!this.geojson || !this.geojson.features) return

    this.geojson.features.forEach(feature => {
      if (!feature.properties) feature.properties = {}
      const props = feature.properties

      if (this.setAllStrokeCheckTarget.checked) {
        this.setOrClear(props, 'stroke', this.setAllStrokeColorTarget.value.trim())
      }
      if (this.setAllStrokeWidthCheckTarget.checked) {
        this.setOrClearNumeric(props, 'stroke-width', this.setAllStrokeWidthTarget.value)
      }
      if (this.setAllStrokeOpacityCheckTarget.checked) {
        this.setOrClearNumeric(props, 'stroke-opacity', this.setAllStrokeOpacityTarget.value)
      }
      if (this.setAllFillCheckTarget.checked) {
        this.setOrClear(props, 'fill', this.setAllFillColorTarget.value.trim())
      }
      if (this.setAllFillOpacityCheckTarget.checked) {
        this.setOrClearNumeric(props, 'fill-opacity', this.setAllFillOpacityTarget.value)
      }
      if (this.setAllNameCheckTarget.checked) {
        this.setOrClear(props, 'name', this.setAllNameTarget.value.trim())
      }
      if (this.setAllDescriptionCheckTarget.checked) {
        this.setOrClear(props, 'description', this.setAllDescriptionTarget.value.trim())
      }
    })

    this.writeBackAndRefresh()
    bootstrap.Modal.getInstance(this.setAllModalTarget).hide()
  }

  // --- Helpers ---

  setOrClear(props, key, value) {
    if (value) {
      props[key] = value
    } else {
      delete props[key]
    }
  }

  setOrClearNumeric(props, key, value) {
    const str = String(value).trim()
    if (str === '') {
      delete props[key]
    } else {
      props[key] = parseFloat(str)
    }
  }

  normalizeColor(color) {
    if (!color) return null
    color = color.trim()
    if (!color.startsWith('#')) color = '#' + color
    // Expand short form #abc to #aabbcc
    if (color.length === 4) {
      color = '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3]
    }
    return /^#[0-9a-fA-F]{6}$/.test(color) ? color : null
  }

  writeBackAndRefresh() {
    // Write formatted GeoJSON back to textarea
    const formatted = JSON.stringify(this.geojson, null, 2)
    this.textarea.value = formatted

    // Clear geojson_filename if present (switching to inline)
    if (this.filenameField) {
      this.filenameField.value = ''
    }

    // Re-render table
    this.renderTable()

    // Dispatch event for preview map to re-render
    this.dispatch('updated', { detail: { geojson: this.geojson } })
  }

  syncColorPicker(event) {
    // Sync color picker to text input (or vice versa)
    const input = event.currentTarget
    const targetId = input.dataset.syncTarget
    if (targetId) {
      const target = document.getElementById(targetId)
      if (target) target.value = input.value
    }
  }

  truncate(str, maxLen) {
    if (!str) return ''
    return str.length > maxLen ? str.substring(0, maxLen) + '...' : str
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
